import React, { useState, useRef, useEffect, useMemo } from 'react';
import { fetchCreatorPortfolioExamples } from '../services/simpleCreatorService';
import './CreatorModal.css';

function CreatorModal({ creator, onClose, onAddToCampaign, onToggleSelect, isSelected, isFavourite, onToggleFavourite, variant, onConfirm, onReject }) {
  const isProposalMode = variant === 'proposal';
  const isPro = creator.tier === 'Pro';
  const [currentPortfolioIndex, setCurrentPortfolioIndex] = useState(0);
  const [selectedVideoIndex, setSelectedVideoIndex] = useState(0);
  const [isVideoPlaying, setIsVideoPlaying] = useState(false);
  const [portfolioExamples, setPortfolioExamples] = useState(creator.examples || []);
  const [loadingPortfolio, setLoadingPortfolio] = useState(false);
  const portfolioRef = useRef(null);
  const mainVideoRef = useRef(null);

  const creatorWithExamples = useMemo(
    () => ({ ...creator, examples: portfolioExamples }),
    [creator, portfolioExamples]
  );

  useEffect(() => {
    let cancelled = false;
    setPortfolioExamples(creator.examples || []);
    setCurrentPortfolioIndex(0);
    setSelectedVideoIndex(0);

    const hasExamples = Array.isArray(creator.examples) && creator.examples.length > 0;
    if (!creator?.id || hasExamples) {
      setLoadingPortfolio(false);
      return undefined;
    }

    setLoadingPortfolio(true);
    fetchCreatorPortfolioExamples(creator.id, creator.niches || [])
      .then((examples) => {
        if (!cancelled) setPortfolioExamples(examples);
      })
      .catch((error) => {
        console.warn('Could not load creator portfolio videos:', error);
        if (!cancelled) setPortfolioExamples([]);
      })
      .finally(() => {
        if (!cancelled) setLoadingPortfolio(false);
      });

    return () => {
      cancelled = true;
    };
  }, [creator]);

  // Handler to play video inline
  const handleVideoPlay = () => {
    if (mainVideoRef.current) {
      // Play immediately without waiting for state
      mainVideoRef.current.play().then(() => {
        setIsVideoPlaying(true);
      }).catch((error) => {
        console.error('Error playing video:', error);
      });
    }
  };

  // Portfolio swipe handlers
  const handlePortfolioSwipe = (direction) => {
    const totalPortfolios = getNichesWithExamples().length;
    if (direction === 'next') {
      setCurrentPortfolioIndex((prev) => (prev + 1) % totalPortfolios);
    } else {
      setCurrentPortfolioIndex((prev) => (prev - 1 + totalPortfolios) % totalPortfolios);
    }
    // Reset selected video when switching niches
    setSelectedVideoIndex(0);
  };

  const normalizeNiche = (value) => (value || '').trim().toLowerCase();

  const getCreatorNiches = () => {
    if (Array.isArray(creator.niches) && creator.niches.length > 0) {
      return creator.niches.map((niche) => niche.trim()).filter(Boolean);
    }
    if (creator.niche) {
      return creator.niche.split('.').map((niche) => niche.trim()).filter(Boolean);
    }
    return [];
  };

  // Get niches that have examples
  const getNichesWithExamples = () => {
    if (!creatorWithExamples.examples || !Array.isArray(creatorWithExamples.examples)) {
      return [];
    }

    const allNiches = getCreatorNiches();
    return allNiches.filter((niche) => {
      const nicheNorm = normalizeNiche(niche);
      return creatorWithExamples.examples.some((example) => {
        const exampleNorm = normalizeNiche(example.niche);
        return exampleNorm === nicheNorm || (!example.niche && niche === allNiches[0]);
      });
    });
  };

  // Get current content videos
  const getCurrentContentVideos = () => {
    if (!creatorWithExamples.examples || !Array.isArray(creatorWithExamples.examples)) {
      return [];
    }
    const nichesWithExamples = getNichesWithExamples();
    if (nichesWithExamples.length === 0) {
      return [];
    }
    const currentNiche = nichesWithExamples[currentPortfolioIndex];
    const currentNorm = normalizeNiche(currentNiche);
    return creatorWithExamples.examples.filter((example) => {
      const exampleNorm = normalizeNiche(example.niche);
      return exampleNorm === currentNorm || (currentPortfolioIndex === 0 && !example.niche);
    });
  };

  // Get current selected video
  const getCurrentSelectedVideo = () => {
    const currentVideos = getCurrentContentVideos();
    return currentVideos[selectedVideoIndex] || currentVideos[0];
  };

  // Handle video selection
  const handleVideoSelect = (index) => {
    setSelectedVideoIndex(index);
    setIsVideoPlaying(false); // Reset playing state when switching videos
  };

  // Reset playing state when portfolio changes
  useEffect(() => {
    setIsVideoPlaying(false);
    if (mainVideoRef.current) {
      mainVideoRef.current.pause();
      mainVideoRef.current.currentTime = 0;
    }
  }, [currentPortfolioIndex, selectedVideoIndex]);

  // Reset portfolio index when creator changes or when niches change
  useEffect(() => {
    const nichesWithExamples = getNichesWithExamples();
    if (currentPortfolioIndex >= nichesWithExamples.length) {
      setCurrentPortfolioIndex(0);
    }
    setSelectedVideoIndex(0);
  }, [creator, currentPortfolioIndex]);

  // Determine if there is content above and below the divider
  const hasAbove = !!(
    creator.bio ||
    (isPro && creator.brands && Array.isArray(creator.brands) && creator.brands.length > 0) ||
    (creatorWithExamples.examples && Array.isArray(creatorWithExamples.examples) && creatorWithExamples.examples.length > 0)
  );
  const hasBelow = !!(
    (creator.notFor && Array.isArray(creator.notFor) && creator.notFor.length > 0) ||
    creator.hobbies ||
    (isPro && creator.metrics && Array.isArray(creator.metrics) && creator.metrics.length > 0)
  );

  return (
    <div className="creator-modal-overlay-unique" onClick={onClose}>
      <div className="creator-modal-container-unique" onClick={e => e.stopPropagation()}>
        {/* Sticky Header with Close Button */}
        <div className="creator-modal-sticky-header-unique">
          <button className="creator-modal-close-btn-unique" onClick={onClose}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
              <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
            </svg>
          </button>
        </div>

        {/* Hero Section */}
        <div className="creator-modal-hero-unique">
          <div className="creator-modal-hero-content-unique">
            <div className="creator-modal-hero-text-unique">
              <div className="hero-greeting-unique">Hello, I'm</div>
              <div className="hero-name-container-unique">
                <h1 className="hero-name-unique">{creator.name}</h1>
                <div className={`creator-badge-inline-unique ${isPro ? 'pro-badge-unique' : 'rising-star-badge-unique'}`}>
                  <svg width="12" height="12" viewBox="0 0 16 16" fill="none">
                    <path d="M8 0L10.5 5.5L16 8L10.5 10.5L8 16L5.5 10.5L0 8L5.5 5.5L8 0Z" fill="currentColor"/>
                  </svg>
                  {isPro ? 'Pro' : 'Rising Star'}
                </div>
              </div>
              <p className="hero-description-unique">
                {creator.bio || `I'm a ${creator.niche?.split('.')[0] || 'content'} creator based in ${creator.location || 'the digital world'}. I create engaging content that connects with audiences and drives results for brands.`}
              </p>
              <div className="hero-age-info-unique">
                {creator.age ? (
                  <span className="age-text-unique">{creator.age} years old</span>
                ) : (
                  <span className="generation-text-unique">{creator.generation || 'Gen Z'}</span>
                )}
              </div>
              <div className="hero-niches-unique">
                {creator.niche.split('.').map((niche, index) => (
                  <span key={index} className="niche-tag-unique">
                    {niche.trim()}
                  </span>
                ))}
              </div>
              {creator.contentSpecialties && creator.contentSpecialties.length > 0 && (
                <div className="hero-content-specialties-unique">
                  {creator.contentSpecialties.slice(0, 3).map((specialty, index) => (
                    <span key={index} className="content-specialty-tag-unique">
                      <svg width="10" height="10" viewBox="0 0 16 16" fill="none">
                        <path d="M8 0L10.5 5.5L16 8L10.5 10.5L8 16L5.5 10.5L0 8L5.5 5.5L8 0Z" fill="currentColor"/>
                      </svg>
                      {specialty}
                    </span>
                  ))}
                </div>
              )}
              {!isProposalMode && (
                <div className="hero-cta-section-unique">
                  <button 
                    className="hero-cta-button-unique"
                    onClick={(e) => {
                      e.stopPropagation();
                      if (onAddToCampaign) onAddToCampaign(creator);
                    }}
                  >
                    Assign Now
                  </button>
                </div>
              )}
            </div>
            <div className="hero-image-unique">
              <div className="profile-avatar-large-unique">
                <img src={creator.profileImage} alt={creator.name} />
              </div>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="profile-content-unique">
          {/* Content Section */}
          {(loadingPortfolio || (creatorWithExamples.examples && creatorWithExamples.examples.length > 0)) && (
            <div className="content-block-unique portfolio-section-unique">
              <div className="portfolio-header-unique">
                <h2>Content</h2>
              </div>

              {loadingPortfolio ? (
                <p className="creator-modal-portfolio-loading">Loading portfolio videos...</p>
              ) : (
              <div className="creator-modal-portfolio-redesign-unique">
                {/* Niche Selection Tabs - Only show if multiple niches with examples */}
                {getNichesWithExamples().length > 1 && (
                  <div className="creator-modal-niche-tabs-unique">
                    {getNichesWithExamples().map((niche, index) => (
                      <button
                        key={index}
                        className={`creator-modal-niche-tab-unique ${currentPortfolioIndex === index ? 'active' : ''}`}
                        onClick={() => {
                          setCurrentPortfolioIndex(index);
                          setSelectedVideoIndex(0);
                        }}
                      >
                        {niche.trim()}
                      </button>
                    ))}
                  </div>
                )}

                {/* Video Showcase */}
                <div className="creator-modal-video-showcase-unique">
                  <div className="creator-modal-video-player-unique">
                    <div className="creator-modal-video-frame-unique" onClick={handleVideoPlay}>
                      <video
                        ref={mainVideoRef}
                        src={getCurrentSelectedVideo()?.video}
                        controls={isVideoPlaying}
                        playsInline
                      />
                      {!isVideoPlaying && (
                        <div className="creator-modal-video-overlay-unique">
                          <div className="creator-modal-play-icon-unique">
                            <svg width="40" height="40" viewBox="0 0 24 24" fill="none">
                              <path d="M8 5V19L19 12L8 5Z" fill="currentColor"/>
                            </svg>
                          </div>
                        </div>
                      )}
                    </div>
                    <div className="creator-modal-video-info-unique">
                      <h4 className="creator-modal-video-title-unique">
                        {getCurrentSelectedVideo()?.label}
                      </h4>
                    </div>
                  </div>

                  {/* Video Gallery */}
                  <div className="creator-modal-video-gallery-unique">
                    <div className="creator-modal-gallery-header-unique">
                      <span className="creator-modal-gallery-title-unique">More Videos</span>
                      <span className="creator-modal-gallery-count-unique">
                        {getCurrentContentVideos().length - 1} videos
                      </span>
                    </div>
                    <div className="creator-modal-gallery-grid-unique">
                      {getCurrentContentVideos().map((example, idx) => {
                        // Skip the currently selected video
                        if (idx === selectedVideoIndex) return null;
                        
                        return (
                          <div 
                            key={idx} 
                            className="creator-modal-gallery-item-unique"
                            onClick={() => handleVideoSelect(idx)}
                          >
                            <div className="creator-modal-gallery-thumbnail-unique">
                              <video
                                src={example.video}
                                muted
                              />
                              <div className="creator-modal-gallery-play-unique">
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                                  <path d="M8 5V19L19 12L8 5Z" fill="currentColor"/>
                                </svg>
                              </div>
                            </div>
                            <div className="creator-modal-gallery-label-unique">
                              {example.label}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              </div>
              )}
            </div>
          )}

          {/* About Section */}
          {creator.about && (
            <div className="content-block-unique about-section-unique">
              <h2>About</h2>
              <p className="bio-text-unique">{creator.about}</p>
            </div>
          )}

          {/* Brands */}
          {isPro && creator.brands && creator.brands.length > 0 && (
            <div className="content-block-unique">
              <h2>Brands I've Worked With</h2>
              <div className="brands-list-unique">
                {creator.brands.map((brand, i) => (
                  <div className="brand-badge-unique" key={i}>{brand}</div>
                ))}
              </div>
            </div>
          )}

          {/* Interests */}
          {creator.hobbies && (
            <div className="content-block-unique">
              <h2>Interests</h2>
              <div className="interests-list-unique">
                {creator.hobbies.split(',').map((hobby, i) => (
                  <span className="interest-tag-unique" key={i}>{hobby.trim()}</span>
                ))}
              </div>
            </div>
          )}

          {/* Not a Fit */}
          {creator.notFor && creator.notFor.length > 0 && (
            <div className="content-block-unique">
              <h2>Not a Fit For</h2>
              <p className="not-fit-text-unique">{creator.notFor.join(', ')}</p>
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="profile-actions-unique">
          <div className="action-buttons-row-unique">
            {isProposalMode ? (
              <>
                <button 
                  className="action-button-unique creator-modal-reject-btn"
                  onClick={(e) => {
                    e.stopPropagation();
                    if (onReject) onReject(creator);
                  }}
                >
                  Reject
                </button>
                <button 
                  className="action-button-unique primary"
                  onClick={(e) => {
                    e.stopPropagation();
                    if (onConfirm) onConfirm(creator);
                  }}
                >
                  Confirm
                </button>
              </>
            ) : (
              <>
                <button 
                  className={`action-button-icon-unique ${isFavourite ? 'active' : ''}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    if (onToggleFavourite) {
                      onToggleFavourite(creator);
                    }
                    setTimeout(() => onClose(), 100);
                  }}
                  title={isFavourite ? 'Remove from Shortlist' : 'Add to Shortlist'}
                  aria-label={isFavourite ? 'Remove from Shortlist' : 'Add to Shortlist'}
                >
                  {isFavourite ? (
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                      <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" fill="currentColor" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  ) : (
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                      <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  )}
                </button>
                <button 
                  className={`action-button-icon-unique ${isSelected ? 'active' : ''}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    if (onToggleSelect) onToggleSelect(creator);
                    setTimeout(() => onClose(), 100);
                  }}
                  title={isSelected ? 'Remove from Selection' : 'Select for Bulk Assignment'}
                  aria-label={isSelected ? 'Remove from Selection' : 'Select for Bulk Assignment'}
                >
                  {isSelected ? '✔️' : '➕'}
                </button>
                <button 
                  className="action-button-unique primary"
                  onClick={(e) => {
                    e.stopPropagation();
                    if (onAddToCampaign) onAddToCampaign(creator);
                  }}
                >
                  Assign Now
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default CreatorModal;